import React, { useMemo, useState } from 'react';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';
import { CheckoutItemParqueType } from '../types';
import {
    DashedLine,
    SummaryCard,
    SummaryContent,
    SummaryHeader,
    SummaryItemStyled,
} from './style';
import { Format } from '../../../helpers/formatters';
import { calculateEffectiveDiscount } from '../../../helpers/utils';
import { useShoppingCartStore } from '@/store/shoppingCart/shoppingCartStore';

type ExpandableContentProps = {
    items: CheckoutItemParqueType[];
};

const ExpandableContent = ({ items }: ExpandableContentProps) => {
    return (
        <SummaryContent>
            {items.map((item, index) => (
                <div className="row" key={index}>
                    <span>{item.title}</span>
                    <span>{Format.currency(item.price)}</span>
                </div>
            ))}
        </SummaryContent>
    );
};

export const CartSummary: React.FC<{
    items: CheckoutItemParqueType[];
    sumCartValues: number;
    sumDiscountValue: number;
}> = ({ items, sumCartValues, sumDiscountValue }) => {
    const [isSessionsExpanded, setSessionsExpanded] = useState(false);
    const [isParksExpanded, setParksExpanded] = useState(false);
    const [isServicesExpanded, setServicesExpanded] = useState(false);
    const coupon = useShoppingCartStore(state => state.coupon);

    const sessionItems = items.filter(item => item.type === 'wave');
    const parkItems = items.filter(item => item.type === 'park');
    const servicesItems = items.filter(item => item.type === 'service');
    const discount = calculateEffectiveDiscount(coupon, sumCartValues);

    const discountLabel = coupon?.percentage ? `Desconto aplicado (${coupon.percentage}%)` : 'Desconto aplicado';
    const finalTotal = coupon 
    ? sumCartValues - discount
    : sumCartValues - sumDiscountValue;

    return (
        <SummaryCard>
            {!!sessionItems.length && (
                <div className="category">
                    <SummaryHeader
                        onClick={() => {
                            setSessionsExpanded(!isSessionsExpanded);
                        }}
                    >
                        <p className="title">Sessões</p>
                        {isSessionsExpanded ? (
                            <MdExpandLess />
                        ) : (
                            <MdExpandMore />
                        )}
                        <span className="quantity">{sessionItems.length}</span>
                    </SummaryHeader>
                    {isSessionsExpanded && (
                        <ExpandableContent
                            items={
                                sessionItems as ExpandableContentProps['items']
                            }
                        />
                    )}
                </div>
            )}

            {!!parkItems.length && (
                <div className="category">
                    <SummaryHeader
                        onClick={() => {
                            setParksExpanded(!isParksExpanded);
                        }}
                    >
                        <p className="title">Ingressos</p>
                        {isParksExpanded ? <MdExpandLess /> : <MdExpandMore />}
                        <span className="quantity">{parkItems.length}</span>
                    </SummaryHeader>

                    {isParksExpanded && (
                        <ExpandableContent
                            items={parkItems as ExpandableContentProps['items']}
                        />
                    )}
                    <DashedLine />
                </div>
            )}

            {!!servicesItems.length && (
                <div className="category">
                    <SummaryHeader
                        onClick={() => {
                            setServicesExpanded(!isServicesExpanded);
                        }}
                    >
                        <p className="title">Serviços</p>
                        {isServicesExpanded ? (
                            <MdExpandLess />
                        ) : (
                            <MdExpandMore />
                        )}
                        <span className="quantity">{servicesItems.length}</span>
                    </SummaryHeader>

                    {isServicesExpanded && (
                        <ExpandableContent
                            items={
                                servicesItems as ExpandableContentProps['items']
                            }
                        />
                    )}
                    <DashedLine />
                </div>
            )}
            <SummaryItemStyled>
                <span>Valor total</span>
                <span>{Format.currency(sumCartValues)}</span>
            </SummaryItemStyled>
            {coupon && (
                <>
                    <SummaryItemStyled style={{ color: '#28a745' }}>
                        <span>{discountLabel}</span>
                        <span>-{Format.currency(discount)}</span>
                    </SummaryItemStyled>
                    <DashedLine />
                    <SummaryItemStyled style={{ fontWeight: 'bold', fontSize: '16px' }}>
                        <span>Total com desconto</span>
                        <span>{Format.currency(finalTotal)}</span>
                    </SummaryItemStyled>
                </>
            )}
            { coupon == null && sumDiscountValue > 0
            ? (
                <>
                    <SummaryItemStyled style={{ color: '#28a745' }}>
                        <span>{discountLabel}</span>
                        <span>-{Format.currency(sumDiscountValue)}</span>
                    </SummaryItemStyled>
                    <DashedLine />
                    <SummaryItemStyled style={{ fontWeight: 'bold', fontSize: '16px' }}>
                        <span>Total com desconto</span>
                        <span>{Format.currency(finalTotal)}</span>
                    </SummaryItemStyled>
                </>
            ) 
            : null
            }
        </SummaryCard>
    );
};
